/**
 * Towns: pieces you place with the build mode (`?build`), and how a town layer is laid onto a map.
 *
 * A town layer (`data/maps/towns.json`, or a local copy in the browser while you build) is a list
 * of pieces, each a kind from PIECES at a tile with a facing, plus tiles painted as path and
 * tiles cleared of whatever the generator grew there. `applyTowns` lays it onto a map file: it
 * clears each piece's footprint, levels the ground under buildings, paints the paths and adds the
 * pieces as ordinary map objects (marked `props.town`, so a newer layer can replace them).
 *
 * The look is scrap and salvage: people built these places out of what the old world left.
 */
import { FLAG_BLOCK, FLAG_WATER, STEEP_LIMIT, WATER_THRESHOLD, decodeI8, decodeU8, encodeLayer, type MapFile, type MapObject, type ObjectType, type PlaceDef } from '../map.js';
import type { StationKind } from './crafting.js';

export type PieceGroup = 'Homes' | 'Work' | 'Farm' | 'Walls' | 'Props' | 'Country';
export interface PieceDef {
  name: string;
  group: PieceGroup;
  /** What it is on the map: a building (blocks), a prop (blocks), decor (you walk through it) or a station. */
  type: Extract<ObjectType, 'house' | 'prop' | 'station' | 'decor' | 'patch'>;
  /** Footprint in tiles at facing 0 (w across x, h across z). */
  w: number;
  h: number;
  /** Stations: what it is (a bank is a 'chest'). */
  station?: StationKind;
  /** Stations that come with a building, in its own tiles (dx, dz) at facing 0, in front of it. */
  adds?: { kind: StationKind; dx: number; dz: number; name?: string }[];
  /** Level the ground under it (buildings). */
  flatten?: boolean;
  /** How much bigger than its model it's drawn: buildings are at the scale of people and stalls (x, y, z, or one number). */
  scale?: number | [number, number, number];
  desc: string;
}

const H = (name: string, w: number, h: number, desc: string, scale = 2): PieceDef => ({ name, group: 'Homes', type: 'house', w, h, flatten: true, desc, scale });
const WK = (name: string, w: number, h: number, desc: string, adds?: PieceDef['adds'], scale = 2): PieceDef => ({ name, group: 'Work', type: 'house', w, h, flatten: true, desc, adds, scale });
const S = (name: string, station: StationKind, desc: string): PieceDef => ({ name, group: 'Work', type: 'station', station, w: 1, h: 1, desc });
const WL = (name: string, desc: string, type: PieceDef['type'] = 'prop', scale: PieceDef['scale'] = [1, 1.6, 1]): PieceDef => ({ name, group: 'Walls', type, w: 1, h: 1, desc, scale });
const F = (name: string, type: PieceDef['type'], w: number, h: number, desc: string): PieceDef => ({ name, group: 'Farm', type, w, h, desc });
const P = (name: string, desc: string, scale: PieceDef['scale'] = 1.3, type: PieceDef['type'] = 'prop', w = 1, h = 1): PieceDef => ({ name, group: 'Props', type, w, h, desc, scale });

// Sizes are real ones, in tiles (about a metre and a half each): a container home is a 20-foot box,
// a bus is a bus. Stations stay as they are, at the size of the market stalls.
export const PIECES = {
  // homes
  container_home: H('Container home', 6, 4, 'A shipping container with a door cut in, a window and a stovepipe.'),
  container_stack: H('Stacked containers', 6, 4, 'Two containers, one on the other, with a ladder and a rail.'),
  bus_home: H('Bus home', 8, 4, 'An old bus shell, wheels gone, windows boarded, lived in for years.'),
  brick_house: H('Patched brick house', 6, 6, 'Old-world brick walls, patched with timber, under a tin roof.'),
  scrap_hut: H('Scrap hut', 4, 4, 'Corrugated sheets on a timber frame. Small, warm, leaks a bit.', 1.75),
  tower_house: H('Watch house', 4, 4, 'A scaffold tower with a cabin on top and a ladder up.'),
  // work buildings, with their stations out front
  bank_hall: WK('Bank vault', 6, 4, 'A concrete bunker with a vault door. The counters out front are the bank.', [{ kind: 'chest', dx: 1, dz: 4, name: 'Bank' }, { kind: 'chest', dx: 4, dz: 4, name: 'Bank' }]),
  smithy: WK('Smithy shed', 6, 4, 'An open-fronted steel shed. A furnace and an anvil out front.', [{ kind: 'furnace', dx: 1, dz: 4 }, { kind: 'anvil', dx: 4, dz: 4 }]),
  cook_house: WK('Cook house', 6, 4, 'A container kitchen with a serving hatch. A range out front.', [{ kind: 'range', dx: 2, dz: 4, name: 'Cook house range' }]),
  workshop: WK('Fletcher\'s workshop', 6, 4, 'A tarp-roofed workshop. A fletching bench out front.', [{ kind: 'workbench', dx: 2, dz: 4, name: 'Fletching bench' }]),
  water_tower: WK('Water tower', 4, 4, 'A rusted tank on four legs. Every town needs one.'),
  // stations on their own
  bank: S('Bank counter', 'chest', 'A safe bolted to a counter. Every bank opens onto the same vault.'),
  deposit: S('Deposit box', 'deposit', 'Put things into your bank from here; nothing comes out.'),
  furnace: S('Furnace', 'furnace', 'Smelt ore into bars.'),
  anvil: S('Anvil', 'anvil', 'Hammer bars into tools, weapons and armour.'),
  range: S('Range', 'range', 'Cook here: it burns less than a fire.'),
  campfire: S('Campfire', 'campfire', 'Cook over it, or just sit by it.'),
  workbench: S('Fletching bench', 'workbench', 'Whittle shafts, fletch arrows and carve bows.'),
  // farming: patches (each character grows their own crop in them) and the quern
  allotment: F('Allotment', 'patch', 5, 2, 'A long bed for vegetables and grain. Everyone farms their own crop in it.'),
  herb_patch: F('Herb patch', 'patch', 2, 2, 'A raised bed for herbs.'),
  tree_patch: F('Tree patch', 'patch', 2, 2, 'A ring of stones for growing your own tree.'),
  fruit_patch: F('Fruit tree patch', 'patch', 2, 2, 'A sheltered square for a fruit tree.'),
  bush_patch: F('Bush patch', 'patch', 2, 2, 'A round bed against a wattle fence, for a berry bush.'),
  mushroom_log: F('Mushroom log', 'patch', 2, 1, 'A rotting log on damp moss, for growing mushrooms.'),
  quern: { ...F('Quern', 'station', 1, 1, 'A hand-turned millstone under a lean-to: wheat into flour.'), station: 'mill' },
  compost_bin: { ...F('Compost bin', 'station', 1, 1, 'A pallet-wood bin for weeds and scraps.'), station: 'compost_bin' },
  scarecrow: P('Scarecrow', 'Old coat, bucket head. The crows are not fooled, but it tries.', 1.3),
  // walls and fences: a tile wide, head-high
  scrap_wall: WL('Scrap wall', 'Corrugated sheets nailed to posts.'),
  palisade: WL('Palisade', 'Sharpened stakes, lashed tight.'),
  tire_wall: WL('Tyre wall', 'Old tyres, stacked and filled with earth.', 'prop', [1, 1.3, 1]),
  chain_fence: WL('Chain-link fence', 'Sagging, but it keeps things out.'),
  gate: WL('Gate', 'A scrap gate, propped open. You can walk through.', 'decor', [1, 1.4, 1]),
  wall_tower: WL('Lookout', 'A railed lookout on legs, with a lantern. Joins the wall it stands in.'),
  // props
  barrels: P('Barrels', 'Rain barrels and fuel drums.'),
  crate_stack: P('Crates', 'Salvage crates, stencilled.'),
  scrap_pile: P('Scrap pile', 'Sorted, mostly.', 1.5),
  car_wreck: P('Car wreck', 'Stripped long ago. Someone grows herbs in it now.', 2, 'prop', 4, 2),
  street_lamp: P('Street lamp', 'An old lamp post, burning oil now.', 1.5),
  sign: P('Sign', 'A hand-painted sign on an old road sign.', 1.4),
  planter: P('Planter', 'A bathtub full of vegetables.', 1.4),
  washing_line: P('Washing line', 'Strung between two poles.', 1.5, 'decor', 3, 1),
  awning: P('Awning', 'A tarp on poles, for shade and trading.', 1.5, 'decor', 3, 3),
  turbine: P('Wind turbine', 'Made from a car fan and a bike wheel. It keeps a lamp lit.', 1.8),
  bench: P('Bench', 'A bus-stop bench, rescued.', 1.3, 'decor'),
  // the country: field boundaries that join up like walls, and the furniture of farms and woods
  hedge: { ...WL('Hedge', 'Thorn and hazel, laid and grown thick. Nothing gets through.', 'prop', 1), group: 'Country' },
  stone_wall: { ...WL('Dry-stone wall', 'Field stones stacked without mortar, older than anyone can remember.', 'prop', 1), group: 'Country' },
  wood_fence: { ...WL('Post-and-rail fence', 'Split rails between posts. Keeps the sheep in, mostly.', 'prop', 1), group: 'Country' },
  field_gate: { ...WL('Field gate', 'A five-bar gate, hung on a stout post. You can walk through.', 'decor', 1), group: 'Country' },
  barn: { ...WK('Barn', 7, 5, 'A big timber barn with a tin roof and doors wide enough for a cart.'), group: 'Country' },
  hay_bales: { ...P('Hay bales', 'Round bales, stacked to dry.', 1, 'prop', 2, 1), group: 'Country' },
  trough: { ...P('Water trough', 'An old bath, fed by a pipe from the roof.', 1, 'prop', 2, 1), group: 'Country' },
  sawpit: { ...P('Sawpit', 'A trestle over a pit, a log on it and a two-man saw.', 1, 'prop', 3, 2), group: 'Country' },
  log_stack: { ...P('Log stack', 'Trunks stacked to season, ends painted against the rot.', 1, 'prop', 3, 1), group: 'Country' },
  woodpile: { ...P('Woodpile', 'Split logs under a lean-to.', 1), group: 'Country' },
  rock_outcrop: { ...P('Rock outcrop', 'Grey rock breaking through the turf, lichen on the top.', 1, 'prop', 2, 2), group: 'Country' },
  boulder: { ...P('Boulder', 'Left here by the ice, long before the End.', 1), group: 'Country' },
  fallen_log: { ...P('Fallen log', 'A tree that came down in a storm, moss on the top.', 1, 'prop', 3, 1), group: 'Country' },
  stump: { ...P('Stump', 'Sawn flat. Somebody sits here at lunch.', 1, 'decor'), group: 'Country' },
  cart: { ...P('Cart', 'A hand cart, its bed full of something.', 1), group: 'Country' },
  tent: { ...P('Tent', 'Canvas over a ridge pole.', 1), group: 'Country' },
  signpost: { ...P('Signpost', 'An arm for each way, the names painted fresh. Its name is what it says.', 1), group: 'Country' },
  minecart: { ...P('Minecart', 'A rusted ore cart, still on a stub of rail.', 1), group: 'Country' },
  spoil_heap: { ...P('Spoil heap', 'Broken rock and clay barrowed out of the dig.', 1, 'prop', 2, 2), group: 'Country' },
  ruin_wall: { ...P('Ruined wall', 'All that stands of something older.', 1), group: 'Country' },
  // the shore and the river
  boat_shed: { ...WK('Boat shed', 6, 4, 'A long timber shed open to the water, a boat half-built inside.'), group: 'Country' },
  watermill: { ...WK('Watermill', 6, 5, 'Stone below, timber above, and a wheel the river still turns.'), group: 'Country' },
  fish_rack: { ...P('Fish rack', 'Fish split and hung to dry in the wind.', 1, 'prop', 3, 1), group: 'Country' },
  rowboat: { ...P('Rowboat', 'Pulled up above the tide line, oars inside.', 1, 'prop', 3, 1), group: 'Country' },
  net_pile: { ...P('Nets', 'Nets heaped to be mended, floats and all.', 1), group: 'Country' },
  driftwood: { ...P('Driftwood', 'Bleached by salt, worn smooth by the sea.', 1, 'decor', 2, 1), group: 'Country' },
  hull: { ...P('Upturned hull', 'A boat hull turned over on posts: a roof, of sorts.', 1, 'prop', 4, 2), group: 'Country' },
  trawler: { ...P('Beached trawler', 'A fishing boat high on the sand, leaning on its side, ribs showing through.', 1, 'prop', 4, 2), group: 'Country' },
  // orchards, chapels and the old road
  apple_tree: { ...P('Apple tree', 'Old, gnarled, still fruiting. Somebody planted it before the End.', 1), group: 'Country' },
  beehive: { ...P('Beehive', 'A box hive on legs. Mind the bees.', 1), group: 'Country' },
  cider_press: { ...P('Cider press', 'A screw press and a tub, sticky with last year\'s apples.', 1, 'prop', 2, 1), group: 'Country' },
  chapel_ruin: { ...WK('Ruined chapel', 6, 8, 'Grey stone, the roof long gone, the bell tower still standing.'), group: 'Country' },
  gravestone: { ...P('Gravestone', 'The name worn smooth. Someone still leaves flowers.', 1), group: 'Country' },
  petrol_canopy: { ...P('Petrol station canopy', 'A tin canopy on four legs over the old pumps. A sign: ROADHOUSE.', 2, 'decor', 6, 4), group: 'Country' },
  fuel_pump: { ...P('Fuel pump', 'Dry for fifty years. The dial still says 0.00.', 1), group: 'Country' },
} satisfies Record<string, PieceDef>;
export type PieceKind = keyof typeof PIECES;
export const PIECE_KINDS = Object.keys(PIECES) as PieceKind[];
export const isPieceKind = (v: unknown): v is PieceKind => typeof v === 'string' && Object.prototype.hasOwnProperty.call(PIECES, v);
export const pieceDef = (k: PieceKind): PieceDef => PIECES[k];
/** How much to scale a piece's model by (x, y, z). */
export function pieceScale(k: string): [number, number, number] {
  const sc = isPieceKind(k) ? (PIECES[k] as PieceDef).scale ?? 1 : 1;
  return typeof sc === 'number' ? [sc, sc, sc] : sc;
}

export interface TownPiece { id: string; kind: PieceKind; x: number; z: number; facing: number; name?: string }
export interface TownFile {
  version: 1;
  /** 2: laid out at the real sizes (buildings twice their first size). Older layers lack it. */
  units?: 2;
  pieces: TownPiece[];
  /** Tiles painted as path. */
  paths: [number, number][];
  /** Tiles cleared of whatever grew or stood there (trees, rocks, old houses and props). */
  clears: [number, number][];
  /** The land: set tiles to this height (in height steps), before anything else. Absolute, so laying
   *  the same layer twice changes nothing. */
  level?: [number, number, number][];
  /** Tiles turned to water (ponds, streams). */
  water?: [number, number][];
  /** Tiles painted another ground (a terrain key: grass, sand, rock, path, mud). */
  paint?: [number, number, string][];
  /** Things to gather, placed by hand (a tree, a rock, a fishing spot): a resource kind at a tile. */
  resources?: { kind: string; x: number; z: number }[];
  /** Creature spawn points: what, where, how many and how far they roam. */
  spawns?: { kind: string; x: number; z: number; count: number; radius: number }[];
  /** Rectangles (x0, z0, x1, z1) whose generated creature spawns are taken away. */
  dropSpawns?: [number, number, number, number][];
  /** Jetties and footbridges: walkable decks over water. */
  bridges?: { kind: string; x: number; z: number; w: number; h: number }[];
  /** Named places (a farm, a clearing): shown when you walk in, and on the map. */
  places?: PlaceDef[];
  /** People who live there: an npc kind at their home tile, with a name for the generic ones. */
  npcs?: { kind: string; x: number; z: number; name?: string }[];
  /** What a layout script laid down itself, so running it again replaces its own paths and clears. */
  layout?: { paths: [number, number][]; clears: [number, number][] };
}
export const emptyTowns = (): TownFile => ({ version: 1, units: 2, pieces: [], paths: [], clears: [] });
/**
 * A layer laid out before buildings went to their real size: spread it out to match. Every
 * position doubles about the layer's middle (so nothing that fitted before overlaps now), and each
 * path or cleared tile becomes the four it covers now.
 */
export function upscaleTowns(t: TownFile): TownFile {
  if (t.units === 2) return t;
  const pts = [...t.pieces.map(p => [p.x, p.z]), ...t.paths, ...t.clears];
  const cx = pts.length ? Math.round(pts.reduce((a, p) => a + p[0], 0) / pts.length) : 0, cz = pts.length ? Math.round(pts.reduce((a, p) => a + p[1], 0) / pts.length) : 0;
  const up = ([x, z]: [number, number] | number[]) => [cx + (x - cx) * 2, cz + (z - cz) * 2] as [number, number];
  const four = (list: [number, number][]) => list.flatMap(t0 => { const [x, z] = up(t0); return [[x, z], [x + 1, z], [x, z + 1], [x + 1, z + 1]] as [number, number][]; });
  return { version: 1, units: 2, pieces: t.pieces.map(p => { const [x, z] = up([p.x, p.z]); return { ...p, x, z }; }), paths: four(t.paths), clears: four(t.clears) };
}

/** Walls and gates join up with their neighbours: each draws a post and a run towards every wall beside it. */
export const WALL_KINDS: readonly PieceKind[] = ['scrap_wall', 'palisade', 'tire_wall', 'chain_fence', 'gate', 'wall_tower', 'hedge', 'stone_wall', 'wood_fence', 'field_gate'];
/** Gates: walls you walk through. */
export const isGate = (k: string) => k === 'gate' || k === 'field_gate';
export const isWall = (k: string) => (WALL_KINDS as readonly string[]).includes(k);
/**
 * Which way each wall or gate joins: bits N 1 (−z), E 2 (+x), S 4 (+z), W 8 (−x) for any wall or
 * gate on that side, and the same bits shifted up 4 for the ones that are gates.
 */
export function wallJoins(pieces: Pick<TownPiece, 'id' | 'kind' | 'x' | 'z'>[]) {
  const at = new Map<string, string>(), out = new Map<string, number>();
  for (const p of pieces) if (isWall(p.kind)) at.set(`${p.x},${p.z}`, p.kind);
  for (const p of pieces) if (isWall(p.kind)) {
    let m = 0;
    for (const [bit, dx, dz] of [[1, 0, -1], [2, 1, 0], [4, 0, 1], [8, -1, 0]] as const) {
      const k = at.get(`${p.x + dx},${p.z + dz}`);
      if (k) m |= bit | (isGate(k) ? bit << 4 : 0);
    }
    out.set(p.id, m);
  }
  return out;
}

/** A piece's footprint at its facing: the tiles it covers, as x, z, w, h. */
export function footprint(p: Pick<TownPiece, 'kind' | 'x' | 'z' | 'facing'>) {
  const d = PIECES[p.kind], turned = p.facing % 2 === 1;
  return { x: p.x, z: p.z, w: turned ? d.h : d.w, h: turned ? d.w : d.h };
}
/**
 * Where a building's added station goes at the piece's facing. (dx, dz) is a tile at facing 0
 * (front is +z); the footprint turns about its centre the same way the model does (facing 1
 * puts the front towards +x).
 */
export function addedAt(p: Pick<TownPiece, 'kind' | 'x' | 'z' | 'facing'>, a: { dx: number; dz: number }): [number, number] {
  const d = PIECES[p.kind], f = footprint(p), th = (((p.facing % 4) + 4) % 4) * Math.PI / 2;
  const lx = a.dx + 0.5 - d.w / 2, lz = a.dz + 0.5 - d.h / 2;
  const rx = lx * Math.cos(th) + lz * Math.sin(th), rz = -lx * Math.sin(th) + lz * Math.cos(th);
  return [Math.floor(f.x + f.w / 2 + rx + 1e-6), Math.floor(f.z + f.h / 2 + rz + 1e-6)];
}

/** Object types a piece clears out of its way. Actors, spawns, obstacles and bridges are left alone. */
const CLEARABLE: ObjectType[] = ['resource', 'prop', 'house', 'station', 'landmark', 'decor', 'patch'];

/**
 * Lay a town layer onto a map file (in place). Earlier town pieces (props.town) are taken off
 * first, so applying a newer layer replaces an older one.
 */
export function applyTowns(file: MapFile, towns: TownFile): MapFile {
  const W = file.width, H2 = file.height;
  const inB = (x: number, z: number) => x >= 0 && z >= 0 && x < W && z < H2;
  let objects = file.objects.filter(o => !o.props?.town);
  const covers = (o: MapObject, x: number, z: number) => x >= o.x && x < o.x + (o.w ?? 1) && z >= o.z && z < o.z + (o.h ?? 1);
  const clearAt = (x: number, z: number) => { objects = objects.filter(o => !(CLEARABLE.includes(o.type) && !o.props?.town && covers(o, x, z))); };
  for (const [x, z] of towns.clears) clearAt(x, z);
  const heights = decodeI8(file.layers.height), terrain = decodeU8(file.layers.terrain), flags = decodeU8(file.layers.flags);
  const PATH = file.terrains.find(t => t.key === 'path')?.id ?? 3;
  const terrainId = (key: string) => file.terrains.find(t => t.key === key)?.id;
  // the land first: levels, then water, then work out again which tiles are water and which too steep
  const touched = new Set<number>();
  for (const [x, z, lv] of towns.level ?? []) if (inB(x, z)) { const t = x + z * W; heights[t] = Math.max(-127, Math.min(127, Math.round(lv))); touched.add(t); }
  const seabed = terrainId('seabed') ?? 4;
  for (const [x, z] of towns.water ?? []) if (inB(x, z)) { const t = x + z * W; heights[t] = Math.min(heights[t], -2); terrain[t] = seabed; touched.add(t); clearAt(x, z); }
  if (touched.size) {
    const unit = file.heightUnit, h = (t: number) => heights[t] * unit, redo = new Set<number>();
    for (const t of touched) for (let dz = -1; dz <= 1; dz++) for (let dx = -1; dx <= 1; dx++) { const x = (t % W) + dx, z = ((t / W) | 0) + dz; if (inB(x, z)) redo.add(x + z * W); }
    for (const t of redo) {
      const x = t % W, z = (t / W) | 0, me = h(t);
      let f = flags[t] & ~(FLAG_WATER | (touched.has(t) ? FLAG_BLOCK : 0));
      if (me < WATER_THRESHOLD) f = (f | FLAG_WATER) & ~FLAG_BLOCK;
      else for (let dz = -1; dz <= 1; dz++) for (let dx = -1; dx <= 1; dx++) {
        const X = x + dx, Z = z + dz;
        if ((!dx && !dz) || !inB(X, Z)) continue;
        const n = h(X + Z * W);
        if (n >= WATER_THRESHOLD && Math.abs(n - me) > STEEP_LIMIT) f |= FLAG_BLOCK;
      }
      flags[t] = f;
    }
  }
  for (const [x, z, key] of towns.paint ?? []) { const id = terrainId(key); if (id !== undefined && inB(x, z) && !(flags[x + z * W] & FLAG_WATER)) terrain[x + z * W] = id; }
  for (const [x0, z0, x1, z1] of towns.dropSpawns ?? []) objects = objects.filter(o => !(o.type === 'creature' && !o.props?.town && o.x >= x0 && o.x <= x1 && o.z >= z0 && o.z <= z1));
  for (const [x, z] of towns.paths) if (inB(x, z) && !(flags[x + z * W] & 2)) terrain[x + z * W] = PATH;
  let next = objects.reduce((m, o) => Math.max(m, o.id), 0) + 1;
  const joins = wallJoins(towns.pieces);
  for (const p of towns.pieces) {
    if (!isPieceKind(p.kind)) continue;
    const d = PIECES[p.kind], f = footprint(p);
    const tiles: [number, number][] = [];
    for (let z = f.z; z < f.z + f.h; z++) for (let x = f.x; x < f.x + f.w; x++) if (inB(x, z)) tiles.push([x, z]);
    const adds = (d.adds ?? []).map(a => ({ ...a, at: addedAt(p, a) }));
    for (const [x, z] of [...tiles, ...adds.map(a => a.at)]) clearAt(x, z);
    // level the ground under a building (only ever on land: water stays water)
    if (d.flatten && tiles.every(([x, z]) => !(flags[x + z * W] & 2))) {
      const avg = Math.round(tiles.reduce((s, [x, z]) => s + heights[x + z * W], 0) / Math.max(1, tiles.length));
      for (const [x, z] of tiles) heights[x + z * W] = avg;
    }
    const props: Record<string, string | number | boolean> = { town: p.id, facing: p.facing % 4 };
    if (p.name) props.name = p.name;
    // a signpost says what it's named
    if (p.kind === 'signpost' && p.name) props.text = p.name;
    if (joins.has(p.id)) props.join = joins.get(p.id)!;
    objects.push({ id: next++, type: d.type, kind: d.type === 'station' ? d.station! : d.type === 'patch' ? ({ herb_patch: 'herb', tree_patch: 'tree', fruit_patch: 'fruit', bush_patch: 'bush', mushroom_log: 'log' } as Record<string, string>)[p.kind] ?? 'allotment' : p.kind, x: f.x, z: f.z, ...(f.w > 1 ? { w: f.w } : {}), ...(f.h > 1 ? { h: f.h } : {}), props: d.type === 'station' ? { ...props, piece: p.kind } : props });
    for (const a of adds) objects.push({ id: next++, type: 'station', kind: a.kind, x: a.at[0], z: a.at[1], props: { town: p.id, ...(a.name ? { name: a.name } : {}) } });
  }
  // things to gather, spawn points and decks, each where it was put
  const taken = new Set<number>();
  for (const o of objects) if (CLEARABLE.includes(o.type)) for (let z = o.z; z < o.z + (o.h ?? 1); z++) for (let x = o.x; x < o.x + (o.w ?? 1); x++) taken.add(x + z * W);
  (towns.resources ?? []).forEach((r, i) => {
    if (!inB(r.x, r.z) || taken.has(r.x + r.z * W)) return;
    objects.push({ id: next++, type: 'resource', kind: r.kind, x: r.x, z: r.z, props: { town: `r${i}` } });
    taken.add(r.x + r.z * W);
  });
  (towns.spawns ?? []).forEach((c, i) => objects.push({ id: next++, type: 'creature', kind: c.kind, x: c.x, z: c.z, props: { town: `c${i}`, count: c.count, radius: c.radius } }));
  (towns.npcs ?? []).forEach((n, i) => objects.push({ id: next++, type: 'npc', kind: n.kind, x: n.x, z: n.z, props: { town: `n${i}`, ...(n.name ? { name: n.name } : {}) } }));
  (towns.bridges ?? []).forEach((b, i) => objects.push({ id: next++, type: 'bridge', kind: b.kind, x: b.x, z: b.z, w: b.w, h: b.h, props: { town: `b${i}` } }));
  if (towns.places?.length) file.places = [...(file.places ?? []).filter(p => !towns.places!.some(q => q.name === p.name)), ...towns.places];
  file.objects = objects;
  file.layers = { ...file.layers, height: encodeLayer(heights), terrain: encodeLayer(terrain), flags: encodeLayer(flags) };
  return file;
}
